import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { X, Receipt, WarningCircle, Coins, CalendarBlank } from "@phosphor-icons/react";

/**
 * UnpaidInvoicesModal — opens from TresoreriePanel when clicking on
 * "Reste à encaisser". Lists every unpaid Axonaut invoice with client
 * name, TTC, amount left to pay and how many days overdue vs due date.
 *
 * Read GET /api/unpaid-invoices (already role-gated admin/compta/associate).
 */
export default function UnpaidInvoicesModal({ open, onClose }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setError(null);
    api
      .get("/unpaid-invoices")
      .then((r) => setRows(r.data || []))
      .catch((err) => setError(formatApiError(err)))
      .finally(() => setLoading(false));
  }, [open]);

  if (!open) return null;

  const fmt = (n) =>
    (Number(n) || 0).toLocaleString("fr-FR", { style: "currency", currency: "EUR", maximumFractionDigits: 2 });

  const daysOverdue = (dueDate) => {
    if (!dueDate) return null;
    const d = new Date(dueDate);
    if (Number.isNaN(d.getTime())) return null;
    const diff = Math.floor((Date.now() - d.getTime()) / 86_400_000);
    return diff;
  };

  const totalLeft = rows.reduce((s, r) => s + (r.left_to_pay || 0), 0);

  return (
    <div
      className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-end md:items-center justify-center p-4"
      data-testid="unpaid-invoices-modal"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-2xl max-h-[85vh] rounded-2xl bg-[#0A0A0A] border border-amber-500/30 shadow-[0_0_40px_rgba(245,158,11,0.15)] flex flex-col overflow-hidden"
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-white/10 bg-gradient-to-r from-amber-500/10 to-transparent">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-amber-500/20 flex items-center justify-center">
              <Coins size={20} weight="duotone" className="text-amber-300" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-amber-300/80">Impayés Axonaut</div>
              <div className="font-display font-semibold text-lg">
                Reste à encaisser · <span className="tabular-nums">{fmt(totalLeft)}</span>
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-9 h-9 rounded-lg hover:bg-white/10 flex items-center justify-center"
            aria-label="Fermer"
            data-testid="unpaid-invoices-close"
          >
            <X size={16} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="space-y-2">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="h-16 rounded-xl bg-white/[0.03] border border-white/5 animate-pulse" />
              ))}
            </div>
          ) : error ? (
            <div className="rounded-xl bg-red-500/10 border border-red-500/30 p-4 flex items-start gap-3" data-testid="unpaid-invoices-error">
              <WarningCircle size={18} weight="fill" className="text-red-400 shrink-0 mt-0.5" />
              <div className="text-sm text-red-300">{error}</div>
            </div>
          ) : rows.length === 0 ? (
            <div className="text-center py-12 text-white/50">
              <Receipt size={40} weight="duotone" className="mx-auto mb-3 text-emerald-400/60" />
              <div className="text-sm">Aucune facture impayée 🎉</div>
              <div className="text-xs text-white/40 mt-1">Ton Axonaut est à jour</div>
            </div>
          ) : (
            <div className="space-y-2" data-testid="unpaid-invoices-list">
              {rows.map((inv) => {
                const overdue = daysOverdue(inv.date_due);
                const isOverdue = overdue !== null && overdue > 0;
                return (
                  <div
                    key={inv.id || inv.number}
                    className={`rounded-xl bg-white/[0.03] border ${isOverdue ? "border-red-500/30" : "border-white/10"} px-3 py-3 flex items-start gap-3`}
                    data-testid={`unpaid-invoice-${inv.id || inv.number}`}
                  >
                    <div className={`w-1 h-full min-h-[36px] rounded-full shrink-0 ${isOverdue ? "bg-red-400" : "bg-amber-400"}`} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-sm truncate max-w-[220px]">
                          {inv.client_name || "Client inconnu"}
                        </span>
                        {inv.number && (
                          <span className="text-[10px] font-mono text-white/40 bg-white/5 px-1.5 py-0.5 rounded">
                            {inv.number}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-3 text-[11px] text-white/50 mt-1 flex-wrap">
                        {inv.date && (
                          <span className="flex items-center gap-1">
                            <CalendarBlank size={11} weight="duotone" />
                            {new Date(inv.date).toLocaleDateString("fr-FR")}
                          </span>
                        )}
                        {inv.date_due && (
                          <span className={isOverdue ? "text-red-300 font-medium" : ""}>
                            Échéance {new Date(inv.date_due).toLocaleDateString("fr-FR")}
                            {isOverdue && ` · +${overdue}j`}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className={`font-display font-bold text-base tabular-nums ${isOverdue ? "text-red-300" : "text-amber-300"}`}>
                        {fmt(inv.left_to_pay)}
                      </div>
                      {inv.total_ttc > (inv.left_to_pay || 0) + 0.01 && (
                        <div className="text-[10px] text-white/40 tabular-nums">
                          sur {fmt(inv.total_ttc)}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
